import type {
  FinancialCharge,
  FinancialComparison,
  FinancialImport,
  FinancialServiceMapping,
} from '@/types/Models/Financial'
import { FINANCIAL_BOOKED_CURRENCY, FINANCIAL_WEIGHT_SOURCES } from '@/const/Financial'
import {
  financialAmountLabel,
  financialAmountsEqual,
  financialDecimal,
  financialDifferenceLabel,
  financialPercent,
  subtractFinancialAmounts,
} from '@/services/FinancialAmounts'
import { financialChargeAmount, financialRole } from '@/services/FinancialImport'
import { financialLoss } from '@/services/FinancialPricing'

const grams = (value: number): string => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(value)

export const financialWeightGrams = (batch: FinancialImport, row: FinancialCharge): number | null => {
  const source = FINANCIAL_WEIGHT_SOURCES[batch.carrier]
  const value = financialDecimal(row.weight)
  if (value === null || Number(value) < 0 || (source.measured && Number(value) === 0)) return null
  return Math.round((source.unit === 'kg' ? Number(value) * 1000 : Number(value)) * 1000) / 1000
}

export const financialServiceMappingId = (carrier: string, product: string): string => {
  return `${carrier}:${product.trim().toUpperCase()}`
}

const priceComparison = (
  batch: FinancialImport,
  row: FinancialCharge,
  purchasePrice: string | null,
): FinancialComparison => {
  const amount = financialChargeAmount(batch, row)
  const tariff = purchasePrice === null ? null : financialDecimal(purchasePrice)
  const result: FinancialComparison = {
    key: 'price',
    label: 'Amount',
    invoice: financialAmountLabel(amount, batch.currency),
    pacsort: tariff === null ? 'No tariff entered' : financialAmountLabel(tariff, batch.currency),
    difference: 'Not compared',
    explanation: 'Enter the purchase tariff from the carrier agreement to check the invoiced amount.',
    state: 'incomplete',
  }
  if (amount === null) return { ...result, explanation: 'The invoice amount is invalid. Check the original row.' }
  if (purchasePrice !== null && tariff === null)
    return { ...result, pacsort: 'Invalid tariff', explanation: 'Enter the tariff as a number, for example 21.50.' }
  if (tariff === null) return result
  if (batch.carrier === 'PostNord' && !batch.amountBasis)
    return { ...result, explanation: 'Confirm how the invoice includes surcharge columns before comparing amounts.' }
  if (!/^[A-Z]{3}$/.test(batch.currency))
    return { ...result, explanation: 'Confirm the invoice currency before comparing the entered purchase tariff.' }
  const delta = subtractFinancialAmounts(amount, tariff)
  const same = financialAmountsEqual(amount, tariff)
  return {
    ...result,
    difference: same ? 'Same as tariff' : financialDifferenceLabel(delta, batch.currency),
    explanation: same
      ? 'The carrier charged the entered purchase tariff.'
      : `The carrier charged ${Number(delta) > 0 ? 'more' : 'less'} than the entered purchase tariff. Check the agreement before deciding whether the carrier made an error.`,
    state: same ? 'same' : 'different',
  }
}

const baseCounts = new WeakMap<FinancialCharge[], Map<string, number>>()

// Delivery rows per shipment number in one invoice, counted once per invoice instead of once per row.
export const financialBaseRowCount = (batch: FinancialImport, trackingNumber: string): number => {
  let counts = baseCounts.get(batch.charges)
  if (!counts) {
    counts = new Map()
    for (const charge of batch.charges)
      if (charge.trackingNumber && charge.review?.status !== 'excluded' && financialRole(batch, charge) === 'base') {
        const key = charge.trackingNumber.trim().toUpperCase()
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
    baseCounts.set(batch.charges, counts)
  }
  return counts.get(trackingNumber.trim().toUpperCase()) ?? 0
}

// PacSort booked this customer price for the delivery when the shipment was created (actualPrice). The difference to the
// carrier cost is 21grams' margin; a cost above it is a loss, usually because the declared weight, size or service was wrong.
const bookedComparison = (batch: FinancialImport, row: FinancialCharge): FinancialComparison => {
  const booked = row.match?.bookedPrice ?? null
  const amount = financialChargeAmount(batch, row)
  const role = financialRole(batch, row)
  const result: FinancialComparison = {
    key: 'booked',
    label: 'Quoted customer price',
    invoice: financialAmountLabel(amount, batch.currency),
    pacsort: booked === null ? 'Not available' : financialAmountLabel(booked, FINANCIAL_BOOKED_CURRENCY),
    difference: 'Cannot compare',
    explanation: "PacSort has no booked customer price for this shipment. Bill the carrier cost plus the customer's margin.",
    state: 'incomplete',
  }
  if (booked === null) return result
  if (role !== 'base')
    return {
      ...result,
      difference: 'Not in the booked price',
      explanation:
        role === 'credit'
          ? "A credit is passed on to the customer with the customer's margin."
          : "This fee was not part of the booked price. It is billed as carrier cost plus the customer's margin.",
      state: 'information',
    }
  if (amount === null) return { ...result, explanation: 'The invoice amount is invalid. Check the original row.' }
  if (batch.currency && batch.currency !== FINANCIAL_BOOKED_CURRENCY)
    return { ...result, explanation: `PacSort books customer prices in SEK; this invoice is in ${batch.currency}.` }
  if (financialBaseRowCount(batch, row.trackingNumber) > 1)
    return { ...result, explanation: 'Several delivery rows share this shipment, so the booked price cannot be allocated to one row.' }
  const margin = subtractFinancialAmounts(booked, amount)
  const loss = financialLoss(batch, row)
  const percent = financialPercent(margin, amount)
  const weight = row.match?.weightGrams ? ` for the booked ${grams(row.match.weightGrams)} g` : ''
  return {
    ...result,
    difference: loss
      ? `Loss ${financialDifferenceLabel(margin, batch.currency)}`
      : `Margin ${financialDifferenceLabel(margin, batch.currency)}${percent ? ` (${percent} on cost)` : ''}`,
    explanation: loss
      ? `The carrier charged more than the price PacSort booked for the customer${weight}. The declared weight, size or service was probably wrong. Bill the booked price and accept the loss, or bill the carrier cost plus the customer's margin.`
      : `PacSort booked this price for the customer${weight}. The difference to the carrier cost is 21grams' margin on the delivery.`,
    state: loss ? 'different' : 'same',
  }
}

const weightComparison = (batch: FinancialImport, row: FinancialCharge): FinancialComparison => {
  const source = FINANCIAL_WEIGHT_SOURCES[batch.carrier]
  const invoice = financialWeightGrams(batch, row)
  const booked = row.match?.weightGrams ?? null
  const unit = `${source.column} (${source.unit})`
  const result: FinancialComparison = {
    key: 'weight',
    label: 'Weight',
    invoice: invoice === null ? (row.weight && Number(row.weight) !== 0 ? `${row.weight} · unreadable` : 'Not reported') : `${grams(invoice)} g`,
    pacsort: booked === null ? 'Not available' : `${grams(booked)} g`,
    difference: 'Cannot compare',
    explanation:
      invoice === null
        ? `The invoice row has no ${unit} value to compare.`
        : 'PacSort has no booked weight for this shipment.',
    state: 'incomplete',
  }
  if (invoice === null || booked === null) return result
  const delta = Math.round((invoice - booked) * 1000) / 1000
  if (!source.measured)
    return {
      ...result,
      difference: delta === 0 ? 'Same as booked' : `${delta > 0 ? '+' : ''}${grams(delta)} g`,
      explanation:
        delta === 0
          ? `${unit} repeats the booked (declared) weight, so it does not confirm a measurement. Measured corrections arrive as separate BLP-ÖMÅTT rows.`
          : `${unit} differs from the booked weight. Check the carrier measurement and the applicable price band.`,
      state: delta === 0 ? 'same' : 'different',
    }
  if (delta === 0)
    return { ...result, difference: 'Same as booked', explanation: `The carrier measured the booked weight (${unit}).`, state: 'same' }
  const percent = booked ? Math.round((delta / booked) * 100) : null
  const sign = delta > 0 ? '+' : ''
  return {
    ...result,
    difference: `${sign}${grams(delta)} g ${delta > 0 ? 'heavier' : 'lighter'}${percent === null ? '' : ` (${sign}${percent}%)`}`,
    explanation:
      delta > 0
        ? `The carrier measured ${grams(invoice)} g; the shipment was booked as ${grams(booked)} g. Check whether the measured weight moves it to a higher price band before deciding the customer amount.`
        : `The carrier measured ${grams(invoice)} g; the shipment was booked as ${grams(booked)} g. A lighter measurement does not add a cost.`,
    state: 'different',
  }
}

export const financialServiceComparison = (
  batch: FinancialImport,
  row: FinancialCharge,
  mappings: FinancialServiceMapping[] = [],
): FinancialComparison => {
  const role = financialRole(batch, row)
  const service = row.match?.service || ''
  const result: FinancialComparison = {
    key: 'service',
    label: role === 'base' ? 'Service' : role === 'credit' ? 'Credit' : 'Additional charge',
    invoice: row.description || 'Not supplied',
    pacsort: service || 'Not available',
    difference: role === 'credit' ? 'Credit row' : 'Additional charge',
    explanation:
      role === 'credit'
        ? 'This row reduces the carrier cost. Decide whether the credit is passed on to the customer.'
        : 'A fee for an existing shipment. Review its customer amount separately from the delivery.',
    state: 'information',
  }
  if (role !== 'base') return result
  if (!service) return { ...result, difference: 'Cannot compare', explanation: 'No booked service is available.', state: 'incomplete' }
  const mapping = mappings.find((item) => item.id === financialServiceMappingId(batch.carrier, row.description))
  if (!mapping)
    return {
      ...result,
      difference: 'Mapping not confirmed',
      explanation: `Confirm once whether ${batch.carrier} product "${row.description}" corresponds to PacSort service "${service}". Rows with the same product are then checked automatically.`,
      state: 'incomplete',
    }
  return mapping.service === service
    ? { ...result, difference: 'Matches confirmed mapping', explanation: `"${row.description}" is confirmed as "${mapping.service}".`, state: 'same' }
    : {
        ...result,
        difference: 'Service differs',
        explanation: `"${row.description}" is confirmed as "${mapping.service}", but this shipment was booked as "${service}". Check whether the carrier charged a different service.`,
        state: 'different',
      }
}

export const financialComparisons = (
  batch: FinancialImport,
  row: FinancialCharge,
  purchasePrice: string | null = row.review?.purchasePrice ?? null,
  mappings: FinancialServiceMapping[] = [],
): FinancialComparison[] => [
  priceComparison(batch, row, purchasePrice),
  bookedComparison(batch, row),
  weightComparison(batch, row),
  financialServiceComparison(batch, row, mappings),
]

export const financialWeightComparison = weightComparison
export const financialBookedComparison = bookedComparison
